import React, { useRef, useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
  TextField,
  Paper,
  Avatar,
  Divider,
  IconButton,
} from '@mui/material';
import {
  Close as CloseIcon,
} from '@mui/icons-material';

import { styled } from '@mui/material/styles';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Carousel from '../components/Carousel';
import ScrollToTop from '../components/ScrollToTop';

import { auth, db } from '../firebase';
import { onAuthStateChanged, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';

// CORREGIDO: HeroSection ocupa todo el ancho
const HeroSection = styled(Box)({
  position: 'relative',
  width: '100%',
  padding: '12rem 5% 8rem 5%',
  // minHeight: 750,
  isolation: 'isolate',
  overflow: 'hidden',
  marginTop: 0,
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(95deg, rgba(0,20,40,0.8) 0%, rgba(0,40,60,0.5) 70%, rgba(0,0,0,0.2) 100%)',
    zIndex: -1,
  },
  '@media (max-width:800px)': {
    padding: '12rem 5% 4rem 5%',
    // minHeight: 650,
  },
});

// CORREGIDO: HeroContent con maxWidth 700 como el original
const HeroContent = styled(Box)({
  flex: '1 1 400px',
  maxWidth: 700,
  color: 'white',
  textShadow: '0 2px 5px rgba(0,0,0,0.3)',
  position: 'relative',
  zIndex: 2,
});

// CORREGIDO: HeroBadge igual al original
const HeroBadge = styled(Box)({
  background: 'rgba(255,232,217,0.9)',
  color: '#b95d00',
  fontWeight: 600,
  padding: '0.3rem 1rem',
  borderRadius: 30,
  display: 'inline-flex',
  alignItems: 'center',
  gap: 8,
  fontSize: '0.9rem',
  marginTop: '0.5rem',
  marginBottom: '1.5rem',
  border: '1px solid #ffd5b5',
  textShadow: 'none',
  '& i': {
    fontSize: '0.9rem',
  },
});

const Highlight = styled('span')({
  color: '#ffb45a',
  borderBottom: '3px solid #ffb45a',
});

// CORREGIDO: Tamaños de texto igual al original
const HeroTitle = styled(Typography)({
  fontSize: '3rem',
  fontWeight: 800,
  lineHeight: 1.2,
  marginBottom: '1.2rem',
  color: 'white',
  '@media (max-width:800px)': {
    fontSize: '2.5rem',
  },
});

const HeroDescription = styled(Typography)({
  fontSize: '1.2rem',
  color: 'rgba(255,255,255,0.95)',
  marginBottom: '2rem',
  maxWidth: 600,
});

const StatNumber = styled(Typography)({
  fontSize: '1.8rem',
  fontWeight: 800,
  color: 'white',
});

const StatLabel = styled(Typography)({
  color: 'rgba(255,255,255,0.8)',
  fontSize: '0.9rem',
});

const HeroImageCard = styled(Paper)({
  flex: '0 1 300px',
  background: 'rgba(255,255,255,0.15)',
  backdropFilter: 'blur(10px)',
  borderRadius: 30,
  padding: '2rem 1.5rem',
  textAlign: 'center',
  boxShadow: '0 25px 40px -10px rgba(0,0,0,0.4)',
  border: '1px solid rgba(255,255,255,0.2)',
  color: 'white',
  maxWidth: '250px',
  marginLeft: 'auto',
});

// CORREGIDO: QuickLogin exactamente como el original
const QuickLogin = styled(Box)({
  background: 'rgba(255,255,255,0.2)',
  backdropFilter: 'blur(8px)',
  borderRadius: 60,
  padding: '0.2rem',
  border: '1px solid rgba(255,255,255,0.35)',
  display: 'flex',
  maxWidth: 260,
});

// CORREGIDO: Input del quick login
const QuickLoginInput = styled('input')({
  border: 'none',
  padding: '0.5rem 1rem',
  borderRadius: 60,
  outline: 'none',
  flex: 1,
  background: 'rgba(255,255,255,0.9)',
  fontSize: '0.95rem',
  '&::placeholder': {
    color: '#666',
    opacity: 1,
  },
});

// CORREGIDO: Botón del quick login
const QuickLoginButton = styled(Button)({
  background: '#0f3b5e',
  border: 'none',
  color: 'white',
  padding: '0.5rem 1.2rem',
  borderRadius: 60,
  fontWeight: 600,
  cursor: 'pointer',
  border: '1px solid white',
  minWidth: 'auto',
  fontSize: '0.95rem',
  textTransform: 'none',
  '&:hover': {
    background: '#154b6e',
  },
});

// CORREGIDO: Botón primary igual al original
const PrimaryButton = styled(Button)({
  background: '#e97c07',
  color: 'white',
  border: 'none',
  padding: '0.9rem 2.2rem',
  borderRadius: 60,
  fontWeight: 600,
  fontSize: '1.1rem',
  boxShadow: '0 8px 16px rgba(0,0,0,0.3)',
  border: '1px solid #e97c07',
  textTransform: 'none',
  '&:hover': {
    background: '#ff941f',
    transform: 'scale(1.02)',
  },
  '& i': {
    fontSize: '1.1rem',
  },
});

// CORREGIDO: Botón outline igual al original
const OutlineButton = styled(Button)({
  background: 'transparent',
  border: '2px solid white',
  color: 'white',
  padding: '0.9rem 2.2rem',
  borderRadius: 60,
  fontWeight: 600,
  fontSize: '1.1rem',
  textTransform: 'none',
  '&:hover': {
    background: 'rgba(255,255,255,0.15)',
    borderWidth: 2,
  },
  '& i': {
    fontSize: '1.1rem',
  },
});

const PillarCard = styled(Card)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.2, 0, 0, 1)',
  transform: 'translateY(0)',
  cursor: 'pointer',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  '&:hover': {
    transform: 'translateY(-8px) scale(1.02)',
    boxShadow: theme.shadows[10],
  },
}));

// CardBg como <img> nativo con loading="lazy" — el navegador descarga la imagen
// solo cuando el pilar entra en el viewport. Antes era un <div> con backgroundImage
// que forzaba la descarga inmediata de las 8 imágenes al montar el componente.
const CardBg = styled('img')({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  objectPosition: 'center',
  zIndex: 0,
  display: 'block',
});

const Overlay = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  background: 'rgba(0,40,60,0.7)',
  zIndex: 1,
});

const CardContentStyled = styled(CardContent)({
  position: 'relative',
  zIndex: 2,
  color: 'white',
  padding: '2rem 1.5rem',
  height: '100%', 
  display: 'flex',
  flexDirection: 'column',
  '&:last-child': {
    paddingBottom: '2rem',
  },
});

const IconWrapper = styled(Avatar)({
  width: 60,
  height: 60,
  borderRadius: 18,
  background: 'rgba(255,255,255,0.2)',
  color: 'white',
  marginBottom: '1.5rem',
  flexShrink: 0, // Evita que se comprima
});

const MembershipCard = styled(Card)(({ premium }) => ({
  borderRadius: 32,
  padding: '2rem 1.8rem',
  height: '100%',
  background: premium ? '#0f3b5e' : 'white',
  color: premium ? 'white' : 'inherit',
}));

const ScrollHint = styled(Box)({
  position: 'absolute',
  bottom: 80,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 5,
  color: 'white',
  fontSize: '0.9rem',
  textShadow: '0 2px 4px rgba(0,0,0,0.5)',
  cursor: 'pointer',
  opacity: 0.8,
  transition: 'opacity 0.3s ease',
  '&:hover': {
    opacity: 1,
    color: '#ffb45a',
  },
});

const ScrollArrow = styled(Box)({
  fontSize: '2rem',
  animation: 'bounce 2s infinite',
  '@keyframes bounce': {
    '0%,20%,50%,80%,100%': { transform: 'translateY(0)' },
    '40%': { transform: 'translateY(10px)' },
    '60%': { transform: 'translateY(5px)' },
  },
});

const pillars = [
  {
    // icon: <i className="fas fa-store"></i>,
    icon: "fa-store",
    title: 'Comercialización 360°',
    description: 'Distribución masiva o especializada, productos físicos/digitales, importación/exportación, dropshipping, tiendas online y marketplaces.',
    image: '/images/image2.jpg',
  },
  {
    // icon: <i className="fas fa-chart-line"></i>,
    icon: "fa-chart-line",
    title: 'Inversión & incubadora',
    description: 'Participamos como socios, accionistas o fundadores en MiPymes, EAT, cooperativas. Incubamos emprendimientos.',
    image: '/images/incubadora.jpg',
  },
  {
    // icon: <i className="fas fa-futbol"></i>,
    icon: "fa-futbol",
    title: 'Eventos · Deporte · Talento',
    description: 'Organización de eventos, representación de deportistas, cazatalentos, gestión de visados, viajes, convenciones.',
    image: '/images/deporte.jpg',
  },
  {
    // icon: <i className="fas fa-coins"></i>,
    icon: "fa-coins",
    title: 'Fintech & activos digitales',
    description: 'Operaciones con criptoactivos, wallets, libranzas, compra de cartera, alianzas con cooperativas y Fintech.',
    image: '/images/fintech.jpg',
  },
  {
    // icon: <i className="fas fa-graduation-cap"></i>,
    icon: "fa-graduation-cap",
    title: 'Educación · intercambio · IA',
    description: 'Diplomados, cursos, talleres; intercambio estudiantil. Desarrollo de software, metaverso, realidad virtual e IA.',
    image: '/images/educacion.jpg',
  },
  {
    // icon: <i className="fas fa-home"></i>,
    icon: "fa-home",
    title: 'Inmobiliario & obra civil',
    description: 'Compra/venta de inmuebles, proyectos de vivienda, constructoras, interventoría, licitaciones.',
    image: '/images/inmobiliario.jpg',
  },
  {
    // icon: <i className="fas fa-heartbeat"></i>,
    icon: "fa-heartbeat",
    title: 'Salud · ARL · Seguros · Mascotas',
    description: 'Planes de salud, pensiones, riesgos laborales, cajas de compensación, previsión exequial humana y para mascotas.',
    image: '/images/seguros.jpg',
  },
  {
    // icon: <i className="fas fa-leaf"></i>,
    icon: "fa-leaf",
    title: 'Energía verde · Sostenibilidad',
    description: 'Proyectos de energías renovables, sello verde, movilidad eléctrica, consultoría ambiental y economía circular.',
    image: '/images/sostenibilidad.jpg',
  },
];

  

export default function LandingPage() {
  const pillarsRef = useRef(null);

  const [pillarsData, setPillarsData] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [editForm, setEditForm] = useState({ title: '', description: '' });

  const [user, setUser] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');

  const renderIcon = (iconName) => {
    return <i className={`fas ${iconName}`}></i>;
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Cargar pillars: primero cache local (render instantáneo) → luego Firestore (fresco)
  // Estrategia stale-while-revalidate. Si Firestore falla (App Check en localhost sin
  // debug token registrado, red, reglas, etc.) el usuario igual ve los datos locales.
  useEffect(() => {
    // 1. Render instantáneo con lo que haya en cache o los defaults locales
    try {
      const cached = localStorage.getItem('pillarsData');
      setPillarsData(cached ? JSON.parse(cached) : pillars);
    } catch {
      setPillarsData(pillars);
    }

    // 2. En background, intentar traer versión fresca de Firestore
    let cancelled = false;
    (async () => {
      try {
        const docRef = doc(db, 'config', 'pillars');
        const docSnap = await getDoc(docRef);
        if (cancelled) return;
        if (docSnap.exists()) {
          const fresh = docSnap.data().items;
          setPillarsData(fresh);
          localStorage.setItem('pillarsData', JSON.stringify(fresh));
        } else {
          // Seed: si el doc no existe en Firestore, lo creamos con los defaults.
          // Puede fallar si las reglas de Firestore no permiten write anónimo — no importa.
          try { await setDoc(docRef, { items: pillars }); } catch {}
        }
      } catch (error) {
        // Silencioso: el usuario ya está viendo los datos del cache/defaults.
        // Solo log en desarrollo para no ensuciar consola en prod.
        if (process.env.NODE_ENV === 'development') {
          console.warn('[pillars] Firestore no disponible, usando datos locales:', error?.code || error?.message);
        }
      }
    })();

    return () => { cancelled = true; };
  }, []);

  // Función para guardar cambios
  // const handleSave = (index) => {
  //   const updatedPillars = [...pillarsData];
  //   updatedPillars[index] = { 
  //     ...updatedPillars[index], 
  //     title: editForm.title, 
  //     description: editForm.description 
  //   };
  //   setPillarsData(updatedPillars);
  //   localStorage.setItem('pillarsData', JSON.stringify(updatedPillars));
  //   setEditingIndex(null);
  // };
  const handleSave = async (index) => {
    const updatedPillars = [...pillarsData];
    updatedPillars[index] = { 
      ...updatedPillars[index], 
      title: editForm.title, 
      description: editForm.description 
    };
    
    setPillarsData(updatedPillars);
    setEditingIndex(null);
    
    // Guardar en Firestore
    try {
      const docRef = doc(db, 'config', 'pillars');
      await setDoc(docRef, { items: updatedPillars });
    } catch (error) {
      console.error('Error guardando en Firestore:', error);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
      setShowLoginModal(false);
      setLoginError('');
      setLoginEmail('');
      setLoginPassword('');
    } catch (error) {
      setLoginError('Credenciales incorrectas');
    }
  };

  const handleLogout = async () => {
    await signOut(auth);
  };

  // Función para iniciar edición
  const startEditing = (index) => {
    setEditForm({
      title: pillarsData[index].title,
      description: pillarsData[index].description
    });
    setEditingIndex(index);
  };

  // const scrollToPillars = () => {
  //   pillarsRef.current?.scrollIntoView({ behavior: 'smooth' });
  // };

  const scrollToPillars = () => {
    const element = document.getElementById('pilares');
    if (element) {
      const offset = 100; // Ajusta este valor según la altura de tu header
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
  };

  // const scrollToPillars = () => {
  //   const element = document.getElementById('pilares');
  //   if (element) {
  //     element.scrollIntoView({ behavior: 'smooth' });
  //   }
  // };





  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* <Header /> */}
      <Header 
        onLoginClick={() => setShowLoginModal(true)}
      />
      {/* Hero Section */}
      <HeroSection id="inicio">
        <Carousel />
        
        <ScrollHint onClick={scrollToPillars}>
          <span>Descubre más</span>
          <ScrollArrow component="span">↓</ScrollArrow>
        </ScrollHint>

        {/* <Container 
          maxWidth={false} 
          sx={{ 
            position: 'relative', 
            zIndex: 2,
            px: { xs: '5%', lg: '5%' },
            maxWidth: '1400px',
            margin: '0 auto',
            width: '100%'
          }}
        > */}
        <Box sx={{ 
        position: 'relative', 
        zIndex: 2,
        px: '5%',
        width: '100%'
        }}>
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <HeroContent>
                <HeroBadge>
                  <i className="fas fa-globe" style={{ fontSize: '0.9rem' }}></i>
                  Objeto social integral · Ley 2069/2020
                </HeroBadge>
                
                <HeroTitle variant="h1">
                  Más que una tienda:<br />
                  <Highlight>tu aliado integral</Highlight><br />
                  en comercialización y negocios
                </HeroTitle>
                
                <HeroDescription>
                  Comercialización, intermediación, representación y club de beneficios con respaldo Fintech, deportivo, inmobiliario, educativo y de salud.
                </HeroDescription>

                <Stack direction="row" spacing={4} sx={{ mb: 3 }}>
                  <Box>
                    <StatNumber>+15</StatNumber>
                    <StatLabel>sectores económicos</StatLabel>
                  </Box>
                  <Box>
                    <StatNumber>∞</StatNumber>
                    <StatLabel>alianzas nacionales</StatLabel>
                  </Box>
                  <Box>
                    <StatNumber>UVT</StatNumber>
                    <StatLabel>membresía desde 1 UVT</StatLabel>
                  </Box>
                </Stack>

                <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 2 }}>
                  <PrimaryButton startIcon={<i className="fas fa-star"></i>}>
                    Conoce el Club
                  </PrimaryButton>
                  <OutlineButton startIcon={<i className="fas fa-handshake"></i>}>
                    Alianzas
                  </OutlineButton>
                </Stack>

                <Box>
                  <QuickLogin>
                    <QuickLoginInput type="text" placeholder="usuario o email" />
                    <QuickLoginButton>
                      <i className="fas fa-arrow-right" style={{ marginRight: '0.4rem' }}></i> 
                      Login
                    </QuickLoginButton>
                  </QuickLogin>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                    <i className="fas fa-lock" style={{ fontSize: '0.8rem' }}></i> Acceso a red privada de usuarios
                  </Typography>
                </Box>
              </HeroContent>
            </Grid>

            <Grid item xs={12} md={5} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <HeroImageCard>
                <i className="fas fa-boxes" style={{ fontSize: '5rem', color: '#e97c07', background: 'rgba(255,255,255,0.2)', padding: '1.2rem', borderRadius: '50%', boxShadow: '0 10px 20px rgba(0,0,0,0.2)' }}></i>
                <Typography variant="h6" sx={{ mb: 1, mt: 1.5 }}>
                  <strong>Comercialización 360°</strong>
                </Typography>
                <Typography sx={{ mb: 2 }}>
                  tradicional · e-commerce · dropshipping · joint venture
                </Typography>
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.2)', my: 2 }} />
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  ✔️ Distribución · Representación · Outsourcing
                </Typography>
              </HeroImageCard>
            </Grid>
          </Grid>
        </Box>
        {/* </Container> */}
      </HeroSection>

      <Box ref={pillarsRef} id="pilares" sx={{ py: 8, bgcolor: 'white' }}>
        <Container maxWidth="xl">
          <Typography variant="h2" align="center" sx={{ mb: 2 }}>
            Nuestra capacidad empresarial
          </Typography>
          <Typography align="center" sx={{ maxWidth: 700, mx: 'auto', mb: 6, color: 'text.secondary' }}>
            Objeto social amplio y flexible: desde inversión en sociedades hasta servicios de salud, pasando por tecnología, construcción y energías renovables.
          </Typography>

          <Grid container spacing={3}>
            {pillarsData.map((pillar, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <PillarCard>
                  <CardBg src={pillar.image} alt={pillar.title} loading="lazy" decoding="async" />
                  <Overlay />
                  <CardContentStyled>
                    {editingIndex === index ? (
                      // MODO EDICIÓN
                      <Box sx={{ position: 'relative', zIndex: 3 }}>
                        <input
                          value={editForm.title}
                          onChange={(e) => setEditForm({...editForm, title: e.target.value})}
                          style={{ 
                            width: '100%', 
                            marginBottom: '10px', 
                            padding: '6px',  // Reducido de 8px a 6px
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            fontSize: '0.9rem'  // Texto más pequeño
                          }}
                        />
                        <textarea
                          value={editForm.description}
                          onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                          style={{ 
                            width: '100%', 
                            marginBottom: '10px', 
                            padding: '6px',  // Reducido de 8px a 6px
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            minHeight: '100px',  // Reducido de 100px a 80px
                            fontSize: '0.9rem'  // Texto más pequeño
                          }}
                        />
                        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                          <Button 
                            variant="contained" 
                            size="small" 
                            onClick={() => handleSave(index)}
                            sx={{ 
                              bgcolor: '#e97c07',
                              fontSize: '0.8rem',  // Texto más pequeño
                              py: 0.5,  // Padding vertical reducido
                              px: 1.5,  // Padding horizontal reducido
                              minWidth: 'auto'
                            }}
                          >
                            Guardar
                          </Button>
                          <Button 
                            variant="contained"  // Cambiado de "outlined" a "contained"
                            size="small" 
                            onClick={() => setEditingIndex(null)}
                            sx={{ 
                              bgcolor: '#6c757d',  // Color gris para el fondo
                              color: 'white',
                              fontSize: '0.8rem',  // Texto más pequeño
                              py: 0.5,  // Padding vertical reducido
                              px: 1.5,  // Padding horizontal reducido
                              minWidth: 'auto',
                              '&:hover': {
                                bgcolor: '#5a6268'  // Color más oscuro al hover
                              }
                            }}
                          >
                            Cancelar
                          </Button>
                        </Stack>
                      </Box>
                    ) : (
                      // MODO VISUALIZACIÓN
<>
  <IconWrapper>
    {renderIcon(pillar.icon)}
  </IconWrapper>
  <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
    {pillar.title}
  </Typography>
  <Typography variant="body2" sx={{ opacity: 0.9 }}>
    {pillar.description}
  </Typography>
  
  {/* Botón de editar - SOLO visible para usuarios autenticados */}
  {user && (   // ← ESTA ES LA CONDICIÓN
    <Button 
      size="small" 
      onClick={() => startEditing(index)}
      sx={{ 
        position: 'absolute', 
        top: 10, 
        right: 10,
        color: 'white',
        zIndex: 3,
        minWidth: 'auto',
        padding: '4px 8px',
        '&:hover': {
          background: 'rgba(255,255,255,0.2)'
        }
      }}
    >
      <i className="fas fa-edit"></i>
    </Button>
  )}
</>
                    )}
                  </CardContentStyled>
                </PillarCard>
              </Grid>
            ))}
          </Grid>

          <Box sx={{ textAlign: 'center', mt: 4 }}>
            <Typography variant="body2" sx={{ color: 'primary.main', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
              <i className="fas fa-file-contract" style={{ color: '#0f3b5e' }}></i> Objeto social no limitativo.
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* Club Section */}
      <Box id="club" sx={{ py: 8, bgcolor: '#f0f6fd' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={5}>
              <Typography variant="h2" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <i className="fas fa-crown" style={{ color: '#e97c07' }}></i> Club empresa privada
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                Red privada de usuarios con beneficios reales en salud, educación, vivienda, recreación, descuentos y alianzas nacionales.
              </Typography>
              <Typography variant="subtitle1" sx={{ mb: 2 }}>
                <strong>Vinculación voluntaria</strong> (según acuerdo de junta de socios).
              </Typography>
              <Paper sx={{ p: 2, bgcolor: '#ffffff60', borderRadius: 4 }}>
                <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <i className="fas fa-check-circle" style={{ color: '#0f3b5e' }}></i> Beneficios en más de 12 áreas.
                </Typography>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <MembershipCard>
                    <i className="fas fa-user" style={{ fontSize: '2rem', color: '#0f3b5e', marginBottom: '1rem' }}></i>
                    <Typography variant="h5" sx={{ mb: 1 }}>Persona Natural</Typography>
                    <Typography variant="h3" sx={{ mb: 0.5 }}>1 UVT <Typography component="span" variant="body2">/ única</Typography></Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>membresía de ingreso</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <i className="fas fa-check" style={{ color: '#e97c07' }}></i> Cuota sostenimiento: 1 UVT/año
                      </Typography>
                      <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <i className="fas fa-check" style={{ color: '#e97c07' }}></i> Acceso a descuentos club
                      </Typography>
                    </Box>
                    <Button variant="outlined" fullWidth>Afiliarme</Button>
                  </MembershipCard>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <MembershipCard premium={1}>
                    <i className="fas fa-building" style={{ fontSize: '2rem', color: 'white', marginBottom: '1rem' }}></i>
                    <Typography variant="h5" sx={{ color: 'white', mb: 1 }}>Persona Jurídica</Typography>
                    <Typography variant="h3" sx={{ color: 'white', mb: 0.5 }}>2 UVT <Typography component="span" variant="body2">/ única</Typography></Typography>
                    <Typography variant="body2" sx={{ color: '#e2eaf3', mb: 2 }}>membresía de ingreso</Typography>
                    <Box sx={{ mb: 2 }}>
                      <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, color: '#f0f7ff' }}>
                        <i className="fas fa-check" style={{ color: '#fed7a5' }}></i> Cuota sostenimiento: 2 UVT/año
                      </Typography>
                      <Typography sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#f0f7ff' }}>
                        <i className="fas fa-check" style={{ color: '#fed7a5' }}></i> Acceso para empleados
                      </Typography>
                    </Box>
                    <Button variant="contained" sx={{ bgcolor: 'white', color: 'primary.main', '&:hover': { bgcolor: '#f5f5f5' } }} fullWidth>
                      Vincular empresa
                    </Button>
                  </MembershipCard>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Emprende Section */}
      <Box id="emprende" sx={{ py: 8, bgcolor: 'white' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={7}>
              <Box sx={{ display: 'inline-block', bgcolor: 'primary.main', color: 'white', px: 2, py: 0.5, borderRadius: 5, mb: 2 }}>
                Ley 2069 de 2020
              </Box>
              <Typography variant="h2" sx={{ mb: 2 }}>Semillero de emprendimiento</Typography>
              <Typography variant="body1" sx={{ color: 'text.secondary', mb: 3 }}>
                Acompañamos tu idea de negocio durante 6 meses. A cambio recibimos el 10% de las utilidades iniciales.
              </Typography>
              <PrimaryButton startIcon={<i className="fas fa-rocket"></i>}>
                Quiero emprender
              </PrimaryButton>
            </Grid>

            <Grid item xs={12} md={5}>
              <Paper sx={{ p: 3, bgcolor: '#f3f9fe', borderRadius: 1 }}>
                <i className="fas fa-rocket" style={{ fontSize: '3rem', color: '#e97c07', marginBottom: '1rem' }}></i>
                <Typography variant="body1">Red de emprendimiento avalada por decreto 1860/2021.</Typography>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Footer />
      <ScrollToTop />

      {/* Modal de Login */}
      {/* {showLoginModal && (
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            bgcolor: 'white',
            p: 4,
            borderRadius: 2,
            zIndex: 9999,
            boxShadow: 24,
            minWidth: 300,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>Login Admin</Typography>
          <form onSubmit={handleLogin}>
            <TextField
              fullWidth
              size="small"
              label="Email"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              sx={{ mb: 2 }}
            />
            {loginError && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {loginError}
              </Typography>
            )}
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Button type="submit" variant="contained" size="small">
                Entrar
              </Button>
              <Button 
                variant="outlined" 
                size="small" 
                onClick={() => setShowLoginModal(false)}
              >
                Cancelar
              </Button>
            </Box>
          </form>
        </Box>
      )} */}

      {/* Modal de Login Profesional */}
{showLoginModal && (
  <Box
    sx={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(5px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
    }}
    onClick={() => setShowLoginModal(false)}
  >
    <Box
      sx={{
        backgroundColor: 'white',
        borderRadius: 1,
        width: '90%',
        maxWidth: 400,
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        animation: 'slideIn 0.3s ease',
        overflow: 'hidden', 
        '@keyframes slideIn': {
          from: {
            transform: 'translateY(-20px)',
            opacity: 0,
          },
          to: {
            transform: 'translateY(0)',
            opacity: 1,
          },
        },
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Header del modal */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0b2b44 0%, #154b6e 100%)',
          color: 'white',
          padding: '1rem 1.5rem',
          // borderTopLeftRadius: 16,
          // borderTopRightRadius: 16,
          position: 'relative',
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
          Acceso Administrativo
        </Typography>
        <Typography variant="body2" sx={{ opacity: 0.8 }}>
          Ingresa tus credenciales para editar el contenido
        </Typography>
        <IconButton
          onClick={() => setShowLoginModal(false)}
          sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            color: 'white',
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.1)',
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Formulario */}
      <Box sx={{ padding: '2rem' }}>
        <form onSubmit={handleLogin}>
          <TextField
            fullWidth
            variant="outlined"
            label="Email"
            type="email"
            value={loginEmail}
            onChange={(e) => setLoginEmail(e.target.value)}
            required
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#e97c07',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#e97c07',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#e97c07',
              },
            }}
          />

          <TextField
            fullWidth
            variant="outlined"
            label="Contraseña"
            type="password"
            value={loginPassword}
            onChange={(e) => setLoginPassword(e.target.value)}
            required
            sx={{
              mb: 3,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                '&:hover fieldset': {
                  borderColor: '#e97c07',
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#e97c07',
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: '#e97c07',
              },
            }}
          />

          {loginError && (
            <Box
              sx={{
                backgroundColor: '#ffebee',
                color: '#c62828',
                padding: '0.75rem',
                borderRadius: 2,
                mb: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <i className="fas fa-exclamation-circle" style={{ fontSize: '1.1rem' }}></i>
              <Typography variant="body2">{loginError}</Typography>
            </Box>
          )}

          <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'flex-end', mt: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setShowLoginModal(false)}
              size="small" 
              sx={{
                borderColor: '#ccc',
                color: '#666',
                fontSize: '0.85rem',
                py: 0.4,      // Padding vertical reducido
                px: 1.8,      // Padding horizontal estándar
                minWidth: '80px',
                '&:hover': {
                  borderColor: '#999',
                  backgroundColor: 'rgba(0,0,0,0.02)',
                },
              }}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              variant="contained"
              size="small"
              sx={{
                backgroundColor: '#e97c07',
                fontSize: '0.85rem',
                py: 0.4,
                px: 1.8,
                minWidth: '80px',
                '&:hover': {
                  backgroundColor: '#ff941f',
                },
                px: 3,
              }}
            >
              Entrar
            </Button>
          </Box>
        </form>
      </Box>

      {/* Footer del modal */}
      <Box
        sx={{
          // padding: '1rem 2rem',
          padding: '0.75rem 1.5rem',
          borderTop: '1px solid #eee',
          backgroundColor: '#f9f9f9',
          // borderBottomLeftRadius: 16,
          // borderBottomRightRadius: 16,
        }}
      >
        <Typography variant="caption" sx={{ color: '#999', display: 'flex', alignItems: 'center', gap: 1 }}>
          <i className="fas fa-shield-alt" style={{ fontSize: '0.9rem' }}></i>
          Acceso restringido solo para administradores
        </Typography>
      </Box>
    </Box>
  </Box>
)}
    </Box>
  );
}